import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { ref, onChildAdded } from 'firebase/database'
import { Container } from '@mui/material'
import { captureEvent } from '@sentry/react'

//components
import Content from './Content'
import QuestAlert from './components/Alerts/Quest'
import PiggyBankChest from './components/Alerts/PiggyBankChest'
import DailyStreakView from './components/DailyStreakView'
import AggregatedReward from './components/Alerts/AggregatedReward'
import Layout from 'components/layout/Games'
import SlideLayout from 'components/layout/Games/Slide'
import BackgroundPigsAnimation from 'components/BackgroundPigsAnimation'
import ConnectGmailModal from 'components/GmailConnection/ConnectGmailModal'
//configs
import { db } from 'config/firebaseConfig'
//constants
import { ERRORS, OFFER_TYPE, ONBOARDING_STEP, REACT_NATIVE_STATUS } from 'constants/index'
//contexts
import NativeAppContext from 'context/NativeApp/NativeAppContext'
import AuthContext from 'context/AuthContext'
import LoaderContext from 'context/LoaderContext'
import HistoryContext from 'context/HistoryContext'
//hooks
import useTOSPromptConfig from 'hooks/useTOSPromptConfig'
import usePiggyBankChestConfig from 'hooks/usePiggyBankChestConfig'
//services
import { getFeaturedGameOffers, getAllUnclaimedRewards } from 'services/userRewardsService'
import { getSuggestedQuest } from 'services/questsService'
//types
import { User } from 'types'
//utils
import { checkDailyStreakVisibility } from 'utils/dailyStreak'
import { handleError, sendRNMessage, showTOSPromptForUser } from 'utils/utils'
import { decodeToken } from 'react-jwt'

export const PREV_ROUTES = ['/library', '/rewards', '/quests']

const GamesList = () => {
  const { user } = useContext(AuthContext)
  const { previousPage } = useContext(HistoryContext)
  const { nativeAppDetails } = useContext(NativeAppContext)

  const [showBackgroundPigsAnimation, setShowBackgroundPigsAnimation] = useState(false)

  const [showParseEnableModal, setshowParseEnableModal] = useState(false)

  const notificationsRef = useRef(null)

  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { isLoading, showLoader, hideLoader } = useContext(LoaderContext)

  const { config: TOSPromptConfig } = useTOSPromptConfig()
  const { piggyChestConfig, isPiggyChestLoading } = usePiggyBankChestConfig()

  useEffect(() => {
    sendRNMessage({
      status: REACT_NATIVE_STATUS.SEND_EVENT_APPSFLYER,
      eventName: 'games_view',
      eventValue: {},
    })
  }, [])

  useEffect(() => {
    if (user) {
      shouldShowParseEnableModal(user)

      if (window.ReactNativeWebView && showTOSPromptForUser(user, TOSPromptConfig?.updatedAt)) {
        navigate('/accept-tos')
        hideLoader()
      }

      if (!user.country) {
        return navigate(`/select-country`)
      }

      const offerActionsRef = ref(db, `offer_actions/${user?.id}`)
      onChildAdded(offerActionsRef, () => {
        queryClient.invalidateQueries({
          queryKey: 'featuredGameOffers',
        })
      })
    }
  }, [user])

  const { data: allUnclaimedRewards, isLoading: loadingAllUnclaimedRewards } = useQuery(
    'allUnclaimedRewards',
    () => getAllUnclaimedRewards(),
    { onError: handleError }
  )

  const { data: offers, isLoading: loadingFeaturedGameOffers } = useQuery(
    'featuredGameOffers',
    () => getFeaturedGameOffers(user?.id),
    {
      onError: handleError,
      enabled: !!user,
      retry: (failureCount, error) => {
        // Handle and report timeout error
        if (error.message.includes('timeout')) {
          captureEvent({
            message: ERRORS.FEATURED_GAME_TIMEOUT_ERROR,
            level: 'error',
            extra: { err: error },
          })

          return failureCount < 1 // Retry only 1 time
        }
        return failureCount < 3 // Retry up to 3 times
      },
    }
  )

  useEffect(() => {
    if (
      offers?.activatedRow.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (offer: any) =>
          offer.offerType === OFFER_TYPE.REWARDED_PLAYTIME || offer.batchPlaytimeConfig
      )
    ) {
      sendRNMessage({
        status: REACT_NATIVE_STATUS.START_PLAYTIME_TRACKING_IN_FOREGROUND,
      })
    }
  }, [offers?.activatedRow])

  const { data: suggestedQuest, isLoading: loadingQuests } = useQuery('quests', getSuggestedQuest, {
    onError: handleError,
  })

  const isFetching =
    isPiggyChestLoading || loadingQuests || loadingFeaturedGameOffers || loadingAllUnclaimedRewards

  useEffect(() => {
    isFetching ? showLoader() : hideLoader()
  }, [isFetching])

  const checkDSVisibility = useCallback(
    () => checkDailyStreakVisibility(Number(nativeAppDetails?.versionCode)),
    [nativeAppDetails?.versionCode]
  )

  const shouldShowParseEnableModal = (user: User) => {
    // [NS] I know I can do this in one if but I dont want to be fetching local storage on every render if the user has parsing enabled
    if (
      !user.hasParsingEnabled &&
      user.onboardingStep === ONBOARDING_STEP.COMPLETE &&
      isLessThan7DaysOld(user.createdAt)
    ) {
      const lastShown = localStorage.getItem('parseEnableModalShown')

      const shouldShowModal = lastShown
        ? // Show if it has been more than 7 days since last shown
          new Date().getTime() - parseInt(lastShown) > 7 * 24 * 60 * 60 * 1000
        : true // Show if never shown before

      if (shouldShowModal) {
        setshowParseEnableModal(true)
        localStorage.setItem('parseEnableModalShown', new Date().getTime().toString())
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isLessThan7DaysOld = (timeStamp: any) => {
    const timestampDate = new Date(timeStamp._seconds * 1000)

    const now = new Date()
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

    return timestampDate > sevenDaysAgo
  }

  return (
    <Layout isLoading={isLoading}>
      {showBackgroundPigsAnimation && <BackgroundPigsAnimation />}
      {user && (!user?.onboardingStep || user?.onboardingStep === ONBOARDING_STEP.COMPLETE) && (
        <Container maxWidth="sm" className="gamesListNotifications" ref={notificationsRef}>
          {checkDSVisibility() && (
            <DailyStreakView setShowBackgroundPigsAnimation={setShowBackgroundPigsAnimation} />
          )}
          {user.isChestAvailableForPurchase && piggyChestConfig && (
            <PiggyBankChest
              config={piggyChestConfig}
              setShowBackgroundPigsAnimation={setShowBackgroundPigsAnimation}
            />
          )}
          {allUnclaimedRewards?.rewards?.length > 0 && (
            <AggregatedReward
              rewards={allUnclaimedRewards?.rewards}
              aggregatedCoins={allUnclaimedRewards?.aggregatedCoins}
              aggregatedPiggyBanks={allUnclaimedRewards?.aggregatedPiggyBanks}
              setShowBackgroundPigsAnimation={setShowBackgroundPigsAnimation}
            />
          )}
          {suggestedQuest && <QuestAlert quest={suggestedQuest} />}
        </Container>
      )}
      <ConnectGmailModal
        isModalDisplayed={showParseEnableModal}
        hideModal={() => {
          setshowParseEnableModal(false)
        }}
      />
      <SlideLayout
        prevRoutes={PREV_ROUTES}
        direction="right"
        swipeIn={!isLoading}
        isSwiping={previousPage?.startsWith('/games/')}
      >
        <Content
          isLoading={isLoading}
          offers={offers}
          previousPage={previousPage}
          notificationsRef={notificationsRef}
        />
      </SlideLayout>
    </Layout>
  )
}

export default GamesList
